// License-related filters

import { CANONICAL_LICENSE_STATUSES } from '@supply-mover-2-0/shared';
import {
	createDateRangeFilter,
	createEntityLookupFilter,
	createKeywordFilter,
} from './factories';
import type { FilterRegistry } from './types';

export const licenseFilters: FilterRegistry = {
	licenseKeywords: {
		// No fulltextFields here on purpose — the shared filter builder would
		// only match against `licenseNumber` and `type`, but licenses also
		// need to match by client name (which lives across the address join).
		// licenses.service.ts handles `licenseKeywords` manually with the
		// proper join. Adding fulltextFields here would cause the shared
		// builder to apply a *narrower* clause that gets ANDed with the
		// service's clause, hiding licenses that only match by client name.
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search licenses...',
			objectTable: 'client_license',
			listKeys: ['license', 'client_licenses', 'license_compliance'],
		}),
		queryParamName: 'licenseKeywords',
	},

	licenseStatus: {
		type: 'multi-select',
		label: 'Status',
		placeholder: 'Select status...',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseStatus',
		multiple: true,
		valueExtractor: {
			property: 'id',
		},
		options: [
			// Derived from the canonical vocabulary (PD-14745) so the dropdown
			// never drifts from lm_status_canonical / the dashboard chart buckets.
			...CANONICAL_LICENSE_STATUSES.map((s) => ({ id: s, label: s })),
			// 'Other' = the catch-all bucket on the dashboard chart: any effective
			// status that isn't one of the canonical values above (legacy
			// free-text on the local column). Surfaced so the chart segment is
			// drillable. The custom handler in typeormFilterBuilder.ts translates
			// this to a NOT IN list.
			{ id: 'Other', label: 'Other' },
		],
		// 'Expired' is not reliably stored on the row — the custom handler in
		// typeormFilterBuilder.ts derives it (stored-'Expired' OR Active with
		// a past expirationDate) over the effective status
		// (COALESCE(registry_status, status)), matching the dashboard KPI and
		// the row-level red pill. 'Active' symmetrically excludes date-expired
		// rows.
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'array',
		listKeys: ['license', 'client_licenses', 'license_compliance'],
	},

	// Restricts licenses by their owning client's lead/customer state.
	// The licenses list joins to client_address → client by default, so
	// the custom handler in typeormFilterBuilder.ts can filter on
	// `client.leadOrCustomer` directly. Used by the compliance dashboard
	// click-throughs to keep the count visible after drilling in
	// matching what the dashboard counted (customers only) — without it,
	// the list shows lead licenses too and the row count diverges from
	// the chart segment.
	licenseClientLeadOrCustomer: {
		type: 'radio',
		label: 'Client Type',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseClientLeadOrCustomer',
		multiple: false,
		options: [
			{ id: 'All', label: 'All' },
			{ id: 'Customer', label: 'Customers' },
			{ id: 'Lead', label: 'Leads' },
		],
		defaultValue: { id: 'All', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		listKeys: ['license', 'client_licenses', 'license_compliance'],
	},

	licenseType: {
		type: 'multi-select',
		label: 'Type',
		placeholder: 'Select type...',
		colorScheme: 'purple',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		apiEndpoint: '/licenses/type/options',
		queryParamName: 'licenseType',
		multiple: true,
		valueExtractor: {
			property: 'id',
		},
		queryMapping: {
			objectTable: 'client_license',
			fieldName: 'type',
			joinType: 'direct',
		},
		format: 'array',
		listKeys: ['license', 'client_licenses', 'license_compliance'],
	},

	// PD-15539 — the single "State" filter (`licenseState`) is RETIRED. It
	// evaluated a COALESCE chain that answered with the issuing board on matched
	// rows and the client's location on everything else, so one label selected
	// two different populations. Product decision 2026-08-24: issuing state and
	// client location are two different facts and must never be conflated in a
	// label, filter or coverage metric. They are now the two filters below.
	//
	// `licenses.service.ts` still ACCEPTS the old `licenseState` key so a saved
	// filter set or bookmarked URL keeps selecting what it always did; it is
	// simply not offered in the tray any more. Do not re-add it here.

	licenseIssuingState: {
		type: 'multi-select',
		label: 'Issuing State',
		placeholder: 'Select issuing state...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		// The registry's own vocabulary, whitelisted to US jurisdictions
		// (PD-14528). This is the axis being filtered — which board granted the
		// licence — so the options come from the registry, not from the tenant's
		// address data.
		apiEndpoint: '/licenses/registry/state/options',
		queryParamName: 'licenseIssuingState',
		multiple: true,
		valueExtractor: {
			property: 'id',
		},
		// No queryMapping on purpose — the issuing state lives on the matched
		// `lm_registry` row, which the shared builder cannot reach from this
		// tenant-local surface. `licenses.service.ts` pre-resolves the matching
		// registry ids in a separate query and then filters locally. Mirrors the
		// `licenseKeywords` pattern above.
		format: 'array',
		// `license` only: the per-client licences list is served by a different
		// endpoint (ClientsService.getClientLicenses, PD-13787) which does not
		// implement these two handlers, so offering them there would no-op.
		listKeys: ['license', 'license_compliance'],
	},

	licenseClientLocationState: {
		type: 'multi-select',
		label: 'Client Location',
		placeholder: 'Select client location...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		apiEndpoint: '/licenses/state/options',
		queryParamName: 'licenseClientLocationState',
		multiple: true,
		valueExtractor: {
			property: 'id',
		},
		// No queryMapping on purpose — the client's location is on the joined
		// `client_address` row, not on `client_license`, so the shared builder
		// would emit a clause against the wrong table. `licenses.service.ts`
		// handles it manually against the `address` alias.
		format: 'array',
		// `license` only — same reason as the issuing-state filter above.
		listKeys: ['license', 'license_compliance'],
	},

	licenseClient: {
		...createEntityLookupFilter({
			label: 'Client',
			placeholder: 'Search clients...',
			apiEndpoint: '/clients',
			objectTable: 'client_license',
			fieldName: 'clientId',
			listKeys: ['license', 'license_compliance'],
			displayFields: {
				id: 'id',
				primary: 'name',
				secondary: 'externalId',
			},
			colorScheme: 'green',
			// `custom` (not `exists`): the actual SQL lives in the manual
			// `licenseClient` branch of LicensesService.findAll. The shared
			// `exists` branch in typeormFilterBuilder requires joinTable +
			// joinConditions metadata that we don't provide, so it no-ops
			// silently. Declaring `custom` makes the intent match reality and
			// prevents a future cleanup from removing the manual handler.
			joinType: 'custom',
		}),
		searchFilterName: 'clientSearch',
		apiParams: {
			page: '1',
			limit: '10',
			fields: JSON.stringify(['client.id', 'client.name', 'client.externalId']),
		},
		queryParamName: 'licenseClient',
		valueExtractor: {
			property: 'id',
		},
	},

	licenseExpirationDateRange: {
		...createDateRangeFilter({
			label: 'Expiration Date',
			objectTable: 'client_license',
			dateField: 'expirationDate',
			listKeys: ['license', 'client_licenses', 'license_compliance'],
		}),
		queryParamName: 'licenseExpirationDateRange',
		allowClear: true,
	},

	licenseHasDisciplinary: {
		type: 'radio',
		label: 'Has Disciplinary Action',
		colorScheme: 'red',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseHasDisciplinary',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'yes', label: 'Yes' },
			{ id: 'no', label: 'No' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			fieldName: 'hasDisciplinaryAction',
			joinType: 'direct',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license', 'client_licenses', 'license_compliance'],
	},

	licenseSalesRep: {
		type: 'multi-select',
		label: 'Sales Rep',
		placeholder: 'Select sales representatives...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
		},
		apiEndpoint: '/licenses/salesrep/options',
		options: [{ id: 'ME', label: 'Me' }],
		queryParamName: 'licenseSalesRep',
		multiple: true,
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license', 'license_compliance'],
	},

	// PD-13955 Phase A: filters by the state board's disciplinary FLAG on the
	// matched registry record (client_license.has_disciplinary_action). This
	// is the flag-only signal — licenses the board marks as having
	// disciplinary history but with no recorded detail rows yet. Distinct
	// from licenseHasDisciplinary above, which matches actual recorded
	// license_disciplinary_action rows (the At Risk tab / dashboard KPI).
	licenseBoardFlagged: {
		type: 'radio',
		label: 'Board Flagged',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseBoardFlagged',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'yes', label: 'Yes' },
			{ id: 'no', label: 'No' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license', 'license_compliance'],
	},

	// PD-13949 / PD-14745: filters matched licenses by whether their linked
	// registry record is under a board action, via the locally-mirrored
	// client_license.registry_status column (populated on confirm, kept fresh
	// by the nightly registry-value re-apply) — tenant-local per PD-13891.
	// `adverse` is the aggregate the Adverse Action tab and the dashboard KPI
	// pin: a confirmed match to a Suspended/Revoked/Surrendered/Denied/Probation
	// state license. (Cancelled/Expired are administrative lapses — "not
	// covered", surfaced via the status/Expired filters, not here.)
	// Link drift (2026-09-05): confirmed licences whose regulator link to
	// another licence the client holds has ENDED (the vet left the premise, the
	// PIC changed). Flag lives on the confirmed match row's match_signals,
	// written by the nightly link-drift stage — tenant-local, no cross-DB join.
	licenseLinkEnded: {
		type: 'radio',
		label: 'Link Ended',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseLinkEnded',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'yes', label: 'Yes' },
			{ id: 'no', label: 'No' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license', 'license_compliance'],
	},

	// PD-16226 — the Compliance dashboard's population and buckets, as list
	// filters, so a KPI card can deep-link to exactly the clients it counted.
	// Before this the "No Active License" card linked to the whole Pending
	// Manual Match tab (4,730 rows for republic against a card reading 82) and
	// no combination of the existing filters could reproduce it.
	licenseActiveBuyer: {
		type: 'radio',
		label: 'Active Buyer',
		colorScheme: 'green',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseActiveBuyer',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'yes', label: 'Buying (sale in 90 days)' },
			{ id: 'no', label: 'Not buying' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license', 'license_compliance'],
	},

	// PD-16256 — the matchmaker found an in-force replacement for this flagged license.
	licenseHasReplacement: {
		type: 'radio',
		label: 'Replacement Available',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseHasReplacement',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'yes', label: 'Matchmaker found an in-force replacement' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license', 'license_compliance'],
	},

	// PD-16255 — the account bought DEA-scheduled product in the last 90 days and
	// holds no verified DEA registration.
	licenseControlledNoDea: {
		type: 'radio',
		label: 'Controlled, No Verified DEA',
		colorScheme: 'red',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseControlledNoDea',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'yes', label: 'Buys controlled product, no verified DEA' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license', 'license_compliance'],
	},

	// PD-16257 — a confirm recorded a correction on this license (what the file
	// showed vs what the regulator shows). Not locked on the Recently Fixed list:
	// the dashboard deep-links one kind at a time.
	licenseRecentlyFixed: {
		type: 'radio',
		label: 'Fixed by Workd',
		colorScheme: 'green',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseRecentlyFixed',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'any', label: 'Any correction' },
			{ id: 'dates', label: 'Expiration date corrected' },
			{ id: 'expired_to_current', label: 'Shown expired, actually current' },
			{ id: 'renewal_captured', label: 'Renewal captured' },
			{ id: 'current_to_expired', label: 'Shown current, actually expired' },
			{ id: 'number_corrected', label: 'Number corrected' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license', 'license_compliance'],
	},

	// PD-16257 — the What Workd Did window a Recently Fixed link was opened from
	// (7 days, 30 days, since go-live). The card counts corrections by when the
	// match was reviewed; without this the list showed every correction ever made
	// under a 7-day figure. Only meaningful together with licenseRecentlyFixed.
	licenseRecentlyFixedWindow: {
		type: 'radio',
		label: 'Fixed Within',
		colorScheme: 'green',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseRecentlyFixedWindow',
		multiple: false,
		options: [
			{ id: 'all', label: 'Since go-live' },
			{ id: 'days7', label: 'Last 7 days' },
			{ id: 'days30', label: 'Last 30 days' },
		],
		defaultValue: { id: 'all', label: 'Since go-live' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license_compliance'],
	},

	// PD-16254 — a license-number match the matchmaker flagged as a COLLISION
	// (match_signals.numberCollision, PD-16247): the number on file is held at the
	// regulator by a business whose name and street number both differ, so the
	// number on file is most likely wrong. PD-16652: on the compliance lists
	// "yes" also takes a PLACEHOLDER number several accounts share ("CLOSED",
	// "bill to only", 123456), resolved into `licensePlaceholderNumberIds`.
	licenseNumberCollision: {
		type: 'radio',
		label: 'Wrong Number on File',
		colorScheme: 'red',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseNumberCollision',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'yes', label: 'Number belongs to another business' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license', 'license_compliance'],
	},

	// PD-16384 — NUMBER NOT FOUND (the "failed lookup" item): a license number
	// on file that the state board, asked on two separate days, says does not
	// exist, and that no other source holds for the state. Not SQL-expressible —
	// the exhausted predicate runs in TypeScript over the mission-control
	// absence store — so the licenses service resolves "yes" to license ids
	// (`licenseFailedLookupIds`) from the same FailedLookupService the dashboard
	// card counts with. Compliance lists only: it has no meaning without that
	// resolution.
	licenseFailedLookup: {
		type: 'radio',
		label: 'Number Not Found',
		colorScheme: 'red',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseFailedLookup',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'yes', label: 'Board has no license with this number' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license_compliance'],
	},

	/**
	 * PD-16384 — INTERNAL, never rendered and never sent by a browser: the
	 * license ids `licenseFailedLookup: yes` resolves to, set by the licenses
	 * service (any inbound value is discarded). On its own it filters nothing.
	 */
	licenseFailedLookupIds: {
		type: 'multi-select',
		label: 'Failed Lookup Licenses',
		colorScheme: 'gray',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseFailedLookupIds',
		multiple: true,
		options: [],
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: [],
	},

	// PD-17113 — DEA REGISTERED AT A DIFFERENT ADDRESS: a confirmed DEA match
	// whose registration the stricter address comparison
	// (crm-api dea-location-separation.ts) does not place at any of the
	// account's locations. A DEA registration covers one location (21 CFR
	// 1301.12(a)). Not SQL-expressible — the comparison runs in TypeScript — so
	// the licenses service resolves "yes" to license ids
	// (`licenseDeaOtherAddressIds`) from the same DeaLocationReviewService the
	// dashboard card counts with. "yes" holds both readings: a truly different
	// location, and one the comparison cannot tell ("may differ — check").
	// Compliance lists only.
	licenseDeaOtherAddress: {
		type: 'radio',
		label: 'DEA at a Different Address',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseDeaOtherAddress',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'yes', label: 'DEA registered at a different address' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license_compliance'],
	},

	/**
	 * PD-17113 — INTERNAL, never rendered and never sent by a browser: the
	 * license ids `licenseDeaOtherAddress: yes` resolves to, set by the
	 * licenses service (any inbound value is discarded). On its own it filters
	 * nothing.
	 */
	licenseDeaOtherAddressIds: {
		type: 'multi-select',
		label: 'DEA Other Address Licenses',
		colorScheme: 'gray',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseDeaOtherAddressIds',
		multiple: true,
		options: [],
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: [],
	},

	// PD-16652 — DUPLICATE LICENSE (Check Your Records): the same license twice
	// on one account, every copy held by the customer's ERP (two or more ERP
	// ids), or the same number under two types on one account. Not
	// SQL-expressible — the license-number key is the matchmaker's TypeScript
	// normalization — so the licenses service resolves "yes" to license ids
	// (`licenseDuplicateGroupIds`) from LicenseDuplicateGroupService, the one
	// the dashboard card counts with. Compliance lists only.
	licenseDuplicateLicense: {
		type: 'radio',
		label: 'Duplicate License',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseDuplicateLicense',
		multiple: false,
		// PD-16653: `reviewed` is the card's Reviewed view — the groups a
		// "Fine as is" or "Being fixed" decision is hiding.
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'yes', label: 'Same license twice on one account' },
			{ id: 'reviewed', label: 'Reviewed: Fine as is or Being fixed' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license_compliance'],
	},

	// PD-16652 — SHARED LICENSE (Check Your Records): one license (type +
	// effective state + normalized number) on two or more customer accounts,
	// with at least one account open. Resolved the same way as Duplicate
	// License, into the same internal id set.
	licenseSharedLicense: {
		type: 'radio',
		label: 'Shared License',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseSharedLicense',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'yes', label: 'One license on several accounts' },
			{ id: 'reviewed', label: 'Reviewed: Fine as is or Being fixed' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license_compliance'],
	},

	/**
	 * PD-16652 — INTERNAL, never rendered and never sent by a browser: the
	 * license ids `licenseDuplicateLicense` / `licenseSharedLicense` resolve
	 * to, set by the licenses service (any inbound value is discarded). On its
	 * own it filters nothing.
	 */
	licenseDuplicateGroupIds: {
		type: 'multi-select',
		label: 'Duplicate Group Licenses',
		colorScheme: 'gray',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseDuplicateGroupIds',
		multiple: true,
		options: [],
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: [],
	},

	/**
	 * PD-16652 — INTERNAL, never rendered and never sent by a browser: the
	 * license ids holding a PLACEHOLDER number ("CLOSED", "bill to only",
	 * 123456) that several accounts share. They are not a shared license, so
	 * they join Wrong Number on File (`licenseNumberCollision: yes`) instead.
	 * Set by the licenses service; on its own it filters nothing.
	 */
	licensePlaceholderNumberIds: {
		type: 'multi-select',
		label: 'Placeholder Number Licenses',
		colorScheme: 'gray',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licensePlaceholderNumberIds',
		multiple: true,
		options: [],
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: [],
	},

	// PD-16252 — the dashboard's Expiring Soon predicate: a verified license whose
	// regulator (else file) expiration falls within 30 days.
	licenseVerifiedExpiringSoon: {
		type: 'radio',
		label: 'Verified, Expiring Soon',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseVerifiedExpiringSoon',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'yes', label: 'Verified license expiring within 30 days' },
			{ id: 'days31to60', label: 'Verified license expiring in 31 – 60 days' },
			{ id: 'days61to90', label: 'Verified license expiring in 61 – 90 days' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license', 'license_compliance'],
	},

	// PD-16252 — the dashboard's ADVERSE ACTION predicate: the regulator's own
	// board-action flag on the linked registry record AND that record still in
	// force (can_order = 1), so the account can keep ordering on it.
	//
	// NOT `licenseBoardFlagged` and NOT `licenseHasDisciplinary`, which both
	// exist above and neither of which can express this card:
	//   licenseBoardFlagged   reads the TENANT's mirrored
	//                         `client_license.has_disciplinary_action`, written
	//                         only at confirm and by the re-apply job, and it
	//                         carries no in-force test — so it counts licences
	//                         the board already killed (they belong to Dead
	//                         License) and misses ones flagged since the mirror
	//                         was last written.
	//   licenseHasDisciplinary reads `license_disciplinary_action` rows, which
	//                         no production tenant writes (see
	//                         HAS_DISCIPLINARY_RECORD in compliance-sql.ts), so
	//                         it reads zero everywhere.
	// This one shares `licenseDisciplinaryInForceSql` with the dashboard card,
	// which is what makes the card count and the list count the same number.
	licenseAdverseInForce: {
		type: 'radio',
		label: 'Board Action, License In Force',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseAdverseInForce',
		multiple: false,
		// PD-16615: one population, three views. `yes` is the with-detail
		// list (PD-16464, unchanged); `flag_only` the board flag with nothing
		// further published; `reviewed` the items a review still hides. The
		// last two are narrowed by `licenseAdverseIncludeIds`, resolved by the
		// licenses service.
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'yes', label: 'Board acted, license still in force' },
			{ id: 'flag_only', label: 'Board flag only, license still in force' },
			{ id: 'reviewed', label: 'Reviewed, hidden until the board changes' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license', 'license_compliance'],
	},

	/**
	 * An INTERNAL id blocklist. Not a user-facing filter: it has no options and
	 * no list ever puts it in `activeFilters`, so it never renders. It exists so
	 * the licenses service can express a predicate SQL cannot compute — today,
	 * compliance-review suppression, whose drift test is a SHA-256 over the
	 * canonicalized board detail (see license-compliance-review-fingerprint.ts).
	 * The service resolves the ids before the query is built and excludes them in
	 * SQL, so the total count and the paging stay exact instead of a page being
	 * post-filtered after the fact.
	 */
	/**
	 * PD-16615 — an INTERNAL id set, the include-side twin of
	 * `licenseExcludeIds` for the Adverse Action Flag Only and Reviewed lists
	 * (`licenseAdverseInForce: flag_only | reviewed`). Never rendered, never
	 * sent by a browser: the licenses service strips any inbound value and
	 * resolves its own.
	 */
	licenseAdverseIncludeIds: {
		type: 'multi-select',
		label: 'Adverse Action Licenses',
		colorScheme: 'gray',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseAdverseIncludeIds',
		multiple: true,
		options: [],
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: [],
	},

	licenseExcludeIds: {
		type: 'multi-select',
		label: 'Excluded Licenses',
		colorScheme: 'gray',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseExcludeIds',
		multiple: true,
		options: [],
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: [],
	},

	/**
	 * PD-16555 — another INTERNAL value, never rendered and never sent by a
	 * browser: the active-buyer CLIENT ids, resolved once per tenant by the
	 * licenses service (and cached briefly) from the dashboard's own population
	 * query. When present it stands in for `licenseActiveBuyer: yes` so that
	 * predicate is not re-evaluated inside every count and every page. On its
	 * own it filters nothing.
	 */
	licenseActiveBuyerClientIds: {
		type: 'multi-select',
		label: 'Active Buyer Clients',
		colorScheme: 'gray',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseActiveBuyerClientIds',
		multiple: true,
		options: [],
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: [],
	},

	// The client's coverage bucket — the same one the dashboard's Coverage bar
	// and Needs Attention cards put each active buyer in. Client-level on
	// purpose: a client is covered once ANY of their licences is verified, so
	// "no active licence" cannot be expressed per licence row.
	licenseClientCoverage: {
		type: 'radio',
		label: 'Client Coverage',
		colorScheme: 'red',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseClientCoverage',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'no_active_license', label: 'No Verified License' },
			{ id: 'unconfirmed', label: 'Ready to Confirm' },
			{ id: 'verified', label: 'Verified' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license', 'license_compliance'],
	},

	// PD-16670 (Chris 2026-10-04) — Account Status: All / Open / Closed, on the
	// shared closed-account rule (crm-api closedAccountClientSql: a flagged
	// closed stage, a stage named Closed, or the ERP's closed signals — inactive
	// status, archived, "Closed Account" business type, CLOSED in the name). It
	// replaces PD-16229's hidden, locked "Hide Closed Accounts" gate on the
	// Pending Manual Match tab: closed accounts stay in every Matching list and
	// a person narrows to them (or away from them) here. The server still reads
	// the old ids ('exclude_closed', 'closed_only') so saved links keep working.
	licenseClientLifecycle: {
		type: 'radio',
		label: 'Account Status',
		colorScheme: 'gray',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseClientLifecycle',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'open', label: 'Open accounts' },
			{ id: 'closed', label: 'Closed accounts' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license', 'license_compliance'],
	},

	// PD-16228 — the three remaining quality classes from the tenant value
	// ledger (maintenance/active/tenant-value-ledger.mjs), as list filters so a
	// reviewer can act on them instead of reading them in a CLI report. The
	// fourth class, a dead license shown as good, is the "Not Usable at State"
	// option on licenseRegistryStanding.
	licenseRegistryGap: {
		type: 'radio',
		label: 'Registry Disagreement',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseRegistryGap',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			// We are refusing or chasing renewals on an account the board says
			// is current — lost orders and wasted rep time.
			{
				id: 'false_block',
				label: 'False Block (we say expired, board says current)',
			},
			// The renewal is already on file at the board; our term is the old
			// one, so this becomes a false block the day it "expires".
			{
				id: 'stale_renewal',
				label: 'Stale Renewal (board holds a later term)',
			},
			// The account holds a lapsed licence while the board shows a
			// different active permit for the same location.
			{ id: 'superseded', label: 'Superseded Permit' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license', 'license_compliance'],
	},

	licenseRegistryStanding: {
		type: 'radio',
		label: 'Regulator Standing',
		colorScheme: 'red',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseRegistryStanding',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			// PD-16225: everything the state will not let the customer use —
			// board actions AND administratively dead licences (Cancelled,
			// Expired, Inactive, Retired, Deceased) — and it counts an
			// unconfirmed 90%+ suggestion as well as a confirmed link.
			{ id: 'unusable', label: 'Dead License' },
			{ id: 'adverse', label: 'Any Adverse Action' },
			{ id: 'Suspended', label: 'Suspended' },
			{ id: 'Revoked', label: 'Revoked' },
			{ id: 'Surrendered', label: 'Surrendered' },
			{ id: 'Denied', label: 'Denied' },
			{ id: 'Probation', label: 'Probation' },
			{ id: 'Limited', label: 'Limited' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license', 'license_compliance'],
	},

	// PD-14745: filter matched licenses by order eligibility (can_order) of the
	// linked registry record — tenant-local via the mirrored registry_status
	// column. `can_order` = valid/in-force (Active/Probation/Limited); `cannot_order` =
	// everything else. Complements the Adverse Action filter above.
	licenseCanOrder: {
		type: 'radio',
		label: 'Order Eligibility',
		colorScheme: 'green',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseCanOrder',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'can_order', label: 'Can Order (valid / in-force)' },
			{ id: 'cannot_order', label: 'Cannot Order' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license', 'license_compliance'],
	},

	// Filters licenses by match status: unmatched (no registry link) or pending review
	licenseMatchStatus: {
		type: 'radio',
		label: 'Match Status',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseMatchStatus',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			// 'matched' is the inverse of 'unmatched' — it selects every
			// license linked to a registry record (fk_registry_license__id
			// IS NOT NULL). Distinct from 'confirmed', which requires a
			// client_license_match row of status 'confirmed'; most matched
			// licenses came in via initial harvest backfill and never had
			// a review record. The compliance-dashboard "Matched" KPI and
			// the "Matched" segment of the By-Match chart drill into this.
			{ id: 'matched', label: 'Matched' },
			{ id: 'unmatched', label: 'Unmatched' },
			{ id: 'under_review', label: 'Under Review' },
			{ id: 'pending_review', label: 'Pending Review' },
			{ id: 'has_pending_match', label: 'Pending Match' },
			{ id: 'confirmed', label: 'Confirmed' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license', 'license_compliance'],
	},

	// Coarse confidence-bucket filter for the License Matchmaker page.
	// Maps to the same client_license_match.confidence_score column as
	// licenseMatchConfidence, but encapsulates the thresholds so the UI
	// and the SQL agree. Boundaries are intentionally open-on-the-upper
	// side for medium/low (50<=x<85, x<50) so decimal scores (the column
	// is decimal(5,2)) don't fall through gaps between buckets.
	licenseMatchConfidenceLevel: {
		type: 'radio',
		label: 'Confidence',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseMatchConfidenceLevel',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'high', label: 'High (≥85%)' },
			{ id: 'medium', label: 'Medium (50–84%)' },
			{ id: 'low', label: 'Low (<50%)' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license', 'license_compliance'],
	},

	// Filters licenses by the confidence score of their pending registry
	// match. Either bound may be omitted. The filter serializes to
	// `{ min, max }` on the wire (either can be null) via the same
	// property-extractor pattern used by date-range filters.
	licenseMatchConfidence: {
		type: 'number-range',
		label: 'Match Confidence (%)',
		placeholder: 'Any',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'licenseMatchConfidence',
		multiple: false,
		queryMapping: {
			objectTable: 'client_license',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'data',
		},
		min: 0,
		max: 100,
		step: 1,
		suffix: '%',
		listKeys: ['license', 'license_compliance'],
	},
};
